// The v3 shell: sidebar, office switcher, topbar, main.
//
// Three navigation GROUPS, and the split is spec ch.06 rather than decoration.
//
// **Workspace** is acceptance V01, word for word: "The customer navigation
// contains Today, Work queue, Administrations, Documents, Bank matching,
// Customer communication, Working rules, Connections and Activity. No Results
// route, savings calculator or customer Results CSV export exists." So `NAV`
// holds exactly those nine and Results is absent — not hidden behind a flag,
// not commented out. Nothing has been added to that list, because V01 says what
// it contains and not what it contains at least.
//
// **Assistant** is Ask Fleur. ch.06 calls it "a read-only status assistant
// attached to the workspace", and the demo attaches it as a topbar button
// rather than a customer navigation item — which is also the only way to add it
// without contradicting V01. It is reachable from both here: a labelled group
// of its own, and the topbar control the demo has.
//
// **Agency** is the operator's own scope, and it is the one group that is not
// about the selected office. ch.06's agency screens are cross-office by
// definition — "implement actual tenant selection rather than reusing one
// office's records" — so they are not customer navigation and must not appear
// as if they were.
//
// The agency group is rendered only for a session that holds an agency operator
// membership somewhere. That is presentation, not a permission: every byte an
// agency screen shows is read through a verified tenant context for one office,
// and the API refuses an office this session is not a member of whatever this
// file renders. Hiding the group from someone who would find every screen empty
// is a kindness; it is not the control.
//
// The screens that are not built yet say so when you open them, because a
// navigation item that silently does nothing is worse than one that explains
// itself: a reviewer needs to know whether the work is missing or the screen is.
//
// A session with no second factor gets the same treatment one layer up. ch.02
// requires MFA for reviewers, owners and agency operators, and the API enforces
// it on every tenant-scoped route — so for such a session there is nothing in
// this office any screen could show. Rendering the screens anyway would produce
// a dozen identical refusals with no explanation between them, and several of
// them would read as "you do not have access", which is the wrong diagnosis:
// the person has the access and the SESSION does not have the assurance. So the
// main area says so once, and the office switcher stays available, because
// another office where this person is only a viewer is still perfectly usable.

import { SearchBox } from './SearchBox.tsx';
import { LOCALES, useLocale, useT, type MessageKey } from '../i18n/index.tsx';
import { Icon, type IconName } from './Icon.tsx';
import { SecondFactorSetup } from './SecondFactor.tsx';
import type { ReactNode } from 'react';
import { agencyMemberships } from '../agency.ts';
import { ALL_ADMINISTRATIONS, useWorkspace } from '../state/workspace.tsx';

/**
 * Where "Beheer" starts in V01's list: Working rules, Connections, Activity —
 * the office's settings and its record, rather than the day's work. The list
 * itself, and its order, is unchanged; this only draws a heading inside it.
 */
function manageFrom(): number {
  return NAV.findIndex((entry) => entry.id === 'rules');
}

export type ScreenId =
  // V01's nine, and nothing else in this union is reachable from `NAV`.
  | 'today'
  | 'work'
  | 'administrations'
  | 'documents'
  | 'bank'
  | 'messages'
  | 'rules'
  | 'connections'
  | 'activity'
  // Attached to the workspace, not in the customer navigation. See above.
  | 'ask'
  // The agency's own scope. Cross-office, operator-only.
  | 'agency-offices'
  | 'agency-onboarding'
  | 'agency-workspace'
  | 'agency-usage';

export interface NavEntry {
  readonly id: ScreenId;
  readonly label: string;
  readonly built: boolean;
}

/** V01's list. Adding to it would make this app fail its own acceptance test. */
export const NAV: readonly NavEntry[] = [
  { id: 'today', label: 'Today', built: true },
  { id: 'work', label: 'Work queue', built: true },
  { id: 'administrations', label: 'Administrations', built: true },
  { id: 'documents', label: 'Documents', built: true },
  { id: 'bank', label: 'Bank matching', built: true },
  { id: 'messages', label: 'Customer communication', built: true },
  { id: 'rules', label: 'Working rules', built: true },
  { id: 'connections', label: 'Connections', built: true },
  { id: 'activity', label: 'Activity', built: true },
];

export const ASSISTANT_NAV: readonly NavEntry[] = [
  { id: 'ask', label: 'Ask Fleur', built: true },
];

/**
 * ch.06's four agency screens, in the order the demo's agency tabs put them.
 *
 * `Office workspace` is one office at a time and the other three are the
 * portfolio, which is why the office it works on is chosen ON that screen
 * rather than in the header: the header's office switcher changes what every
 * customer screen shows, and an agency operator moving between offices to
 * prepare a test should not be dragging the whole workspace with them.
 */
export const AGENCY_NAV: readonly NavEntry[] = [
  { id: 'agency-offices', label: 'Offices', built: true },
  { id: 'agency-onboarding', label: 'Onboarding', built: true },
  { id: 'agency-workspace', label: 'Office workspace', built: true },
  { id: 'agency-usage', label: 'Costs & usage', built: true },
];

/** Every entry in every group, for a lookup that must not miss one. */
export const ALL_NAV: readonly NavEntry[] = [...NAV, ...ASSISTANT_NAV, ...AGENCY_NAV];

/**
 * True for a screen whose subject is the portfolio rather than the selected
 * office.
 *
 * The header's administration selector is hidden for these. An administration
 * belongs to one office, and offering one while the screen is showing four
 * would suggest it filters them — it cannot, and a control that looks like it
 * narrows what you are reading and does not is worse than no control.
 */
export function isAgencyScreen(screen: ScreenId): boolean {
  return screen.startsWith('agency-');
}

/** The design's icon for each navigation item. Decoration; never the name. */
const NAV_ICON: Readonly<Record<ScreenId, IconName>> = {
  today: 'grid',
  work: 'review',
  administrations: 'home',
  documents: 'file',
  bank: 'swap',
  messages: 'chat',
  rules: 'sliders',
  connections: 'plug',
  activity: 'activity',
  ask: 'sparkle',
  'agency-offices': 'building',
  'agency-onboarding': 'target',
  'agency-workspace': 'grid',
  'agency-usage': 'euro',
};

const ROLES = new Set(['owner', 'reviewer', 'viewer', 'agency_operator']);

function initials(text: string): string {
  return text
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export interface ShellProps {
  readonly screen: ScreenId;
  readonly onNavigate: (screen: ScreenId) => void;
  /** Open work in the selection, for the live badge on the work queue. */
  readonly openWork: number | null;
  /** Where a search result goes. Without it there is no search box. */
  readonly onOpenDocument?: (documentId: string) => void;
  readonly children: ReactNode;
}

export function Shell({ screen, onNavigate, openWork, onOpenDocument, children }: ShellProps): ReactNode {
  const workspace = useWorkspace();
  const t = useT();
  const current = ALL_NAV.find((entry) => entry.id === screen);
  // The offices this session serves as an agency operator. Read from GET /me,
  // which reports live memberships and nothing else, so an office whose
  // membership was revoked leaves the portfolio on the next sign-in read.
  const agencyOffices = agencyMemberships(workspace.me);
  const agency = isAgencyScreen(screen);

  return (
    <div className="shell">
      <aside className="sidebar">
        {/* The panel runs the full page height; its contents stay in view. */}
        <div className="sidebar-inner">
          <div className="branding">
            <div className="mark" aria-hidden="true">
              <Icon name="brand" size={20} />
            </div>
            <div>
              <b>AI Bookkeeper</b>
              <small>{t('shell.byline')}</small>
            </div>
          </div>

          <OfficePicker agency={agency} />

          <nav className="nav" aria-label={t('nav.main')}>
            <div className="nav-list">
              {NAV.slice(0, manageFrom()).map((entry) => (
                <NavButton
                  key={entry.id}
                  entry={entry}
                  screen={screen}
                  onNavigate={onNavigate}
                  count={entry.id === 'work' ? openWork : null}
                />
              ))}
            </div>
            <div className="nav-list">
              <div className="nav-group">{t('nav.group.manage')}</div>
              {NAV.slice(manageFrom()).map((entry) => (
                <NavButton key={entry.id} entry={entry} screen={screen} onNavigate={onNavigate} count={null} />
              ))}
            </div>
          </nav>

          <nav className="nav nav-list" aria-label={t('nav.assistantNav')}>
            <div className="nav-group">{t('nav.group.assistant')}</div>
            {ASSISTANT_NAV.map((entry) => (
              <NavButton key={entry.id} entry={entry} screen={screen} onNavigate={onNavigate} count={null} />
            ))}
          </nav>

          {agencyOffices.length === 0 ? null : (
            <nav className="nav nav-list" aria-label={t('nav.agencyNav')}>
              <div className="nav-group">{t('nav.group.agency')}</div>
              {AGENCY_NAV.map((entry) => (
                <NavButton
                  key={entry.id}
                  entry={entry}
                  screen={screen}
                  onNavigate={onNavigate}
                  count={null}
                />
              ))}
            </nav>
          )}

          <div className="sidefoot">
            <div className="profile">
              <div className="avatar ink">{initials(workspace.me.user.displayName ?? workspace.me.user.email)}</div>
              <div className="grow" title={workspace.me.user.email}>
                {/* The name the control plane set (0032); the address until then. */}
                <b className="profile-name">{workspace.me.user.displayName ?? workspace.me.user.email}</b>
                {/* The role the DATABASE confirmed for this office, not a claim
                    from the token: GET /me reports what begin_request signed. */}
                <small>
                  {ROLES.has(workspace.membership.role)
                    ? t(`role.${workspace.membership.role}` as MessageKey)
                    : workspace.membership.role.replace('_', ' ')}
                </small>
              </div>
            </div>
            <div className="sidefoot-row">
              <LanguageSwitch />
              <button type="button" className="btn text" onClick={() => workspace.signOut()}>
                {t('shell.signOut')}
              </button>
            </div>
          </div>
        </div>
      </aside>

      <div className="wrap">
        <header className="topbar">
          <div className="crumb">
            {agency ? t('nav.group.agency') : t('nav.group.workspace')}
            <span className="crumb-sep">/</span>
            <b>{current === undefined ? t('nav.group.workspace') : t(`nav.${current.id}`)}</b>
          </div>
          {/* Documents, within this office and the session's grants. Not on an
              agency screen, which spans offices: a search there would have to
              say which office it searched, and GET /search answers for one. */}
          {agency || onOpenDocument === undefined ? null : <SearchBox onOpenDocument={onOpenDocument} />}
          <div className="flex">
            {/* The demo's topbar control, kept because ch.06 attaches Fleur to
                the workspace rather than filing it under a screen. It navigates
                to the same screen the Assistant group does; there is one Ask
                Fleur, reachable from two places. */}
            <button
              type="button"
              className="btn"
              onClick={() => onNavigate('ask')}
              aria-current={screen === 'ask' ? 'page' : undefined}
            >
              <span className="icon-green">
                <Icon name="sparkle" />
              </span>
              {t('nav.ask')}
            </button>
            <div className="avatar ink" title={workspace.me.user.email}>
              {initials(workspace.me.user.displayName ?? workspace.me.user.email)}
            </div>
          </div>
        </header>
        <main className="main">
          {workspace.membership.mfaSatisfied ? children : <SecondFactorRequired />}
        </main>
      </div>
    </div>
  );
}

/**
 * What GET /me's `mfaSatisfied: false` means, said once and in the office's own
 * terms.
 *
 * It offers the one control that resolves it: setting up an authenticator,
 * which the API can now do (components/SecondFactor.tsx). Until it could, this
 * screen had no button, because a control that does nothing is the kind of
 * screen this codebase keeps refusing to build.
 */
function SecondFactorRequired(): ReactNode {
  const workspace = useWorkspace();
  return (
    <>
      <div className="heading">
        <h1>This session needs a second factor</h1>
        <p>Nothing in this office can be read or changed until it has one.</p>
      </div>
      <div className="card">
        <div className="empty">
          <h3>Your access is not the problem</h3>
          <p>
            You are a <b>{workspace.membership.role.replace(/_/g, ' ')}</b> in{' '}
            <b>{workspace.membership.tenantName}</b>, and that is exactly why: owners, reviewers
            and agency operators sign in with multi-factor authentication. The session you are
            holding was issued without it.
          </p>
          <p style={{ marginTop: 10 }}>
            Set up an authenticator app to continue. The same requirement applies to every screen
            in this office, so nothing here is hidden that a different screen would show.
          </p>
          <div style={{ marginTop: 16 }}>
            <SecondFactorSetup />
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * The design's office box: the office on the first line, the administration on
 * the second, a chevron. Each line is a real <select> laid over its text, so it
 * is a native dropdown for keyboard, screen reader and touch alike. The office
 * line only becomes one when this person is in more than one office.
 *
 * Not on an agency screen: those span offices, and an administration belongs
 * to one (see isAgencyScreen), so the second line says so instead.
 */
function OfficePicker({ agency }: { readonly agency: boolean }): ReactNode {
  const workspace = useWorkspace();
  const t = useT();
  const offices = workspace.me.memberships;
  const administration =
    workspace.administrationId === ALL_ADMINISTRATIONS
      ? t('shell.allAdministrations')
      : (workspace.administrations.find((entry) => entry.id === workspace.administrationId)?.name ??
        t('shell.allAdministrations'));
  return (
    <div className="office-picker" data-testid="office-picker">
      <div className="office-lines">
        <span className="picker-line office-name">
          {workspace.membership.tenantName}
          {offices.length > 1 ? (
            <select
              aria-label={t('shell.office')}
              value={workspace.membership.tenantId}
              onChange={(event) => workspace.selectTenant(event.target.value)}
            >
              {offices.map((membership) => (
                <option key={membership.tenantId} value={membership.tenantId}>
                  {membership.tenantName}
                </option>
              ))}
            </select>
          ) : null}
        </span>
        <span className="picker-line administration-name">
          {agency ? t('shell.acrossOffices') : administration}
          {agency ? null : (
            <select
              aria-label={t('shell.selectAdministration')}
              value={workspace.administrationId}
              onChange={(event) => workspace.selectAdministration(event.target.value)}
            >
              <option value={ALL_ADMINISTRATIONS}>{t('shell.allAdministrations')}</option>
              {workspace.administrations.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </select>
          )}
        </span>
      </div>
      <span className="office-chev" aria-hidden="true">
        <Icon name="chev" size={16} />
      </span>
    </div>
  );
}

function NavButton({
  entry,
  screen,
  onNavigate,
  count,
}: {
  readonly entry: NavEntry;
  readonly screen: ScreenId;
  readonly onNavigate: (screen: ScreenId) => void;
  readonly count: number | null;
}): ReactNode {
  const t = useT();
  return (
    <button
      type="button"
      className={entry.id === screen ? 'active' : ''}
      {...(entry.id === screen ? { 'aria-current': 'page' as const } : {})}
      onClick={() => onNavigate(entry.id)}
      data-testid={`nav-${entry.id}`}
    >
      <span className="nav-icon" aria-hidden="true">
        <Icon name={NAV_ICON[entry.id]} size={18} />
      </span>
      <span className="nav-label">{t(`nav.${entry.id}`)}</span>
      {count !== null && count > 0 ? (
        <span className="count" data-testid={`nav-count-${entry.id}`} title={t('nav.waitingBadge', { count })}>
          {count}
        </span>
      ) : null}
    </button>
  );
}

/** NL | EN. The choice is remembered (see i18n/index.tsx). */
export function LanguageSwitch(): ReactNode {
  const { locale, setLocale } = useLocale();
  const t = useT();
  return (
    <div className="lang-switch" role="group" aria-label={t('shell.language')} data-testid="language-switch">
      {LOCALES.map((option) => (
        <button
          key={option}
          type="button"
          aria-pressed={locale === option}
          data-testid={`language-${option}`}
          onClick={() => setLocale(option)}
        >
          {option.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

/** What an unbuilt navigation item says. Honest, and specific about why. */
export function NotBuiltYet({ label }: { readonly label: string }): ReactNode {
  return (
    <>
      <div className="heading">
        <h1>{label}</h1>
        <p>This screen is not built yet.</p>
      </div>
      <div className="card">
        <div className="empty">
          <h3>Nothing is hidden here</h3>
          <p>
            The navigation shows every screen the product will have, so this one is listed. It has
            no data behind it yet and shows nothing rather than a placeholder that could be mistaken
            for real work.
          </p>
        </div>
      </div>
    </>
  );
}
